import { useRef, useState } from 'react';
import { useSite } from '../hooks/useSite';
import { PAGE, pick } from '../lib/content';
import { ActionLink, Eyebrow, PageHero, TextLink } from '../components/Primitives';
import RichText from '../components/RichText';

const fields = [
  { name: 'name', label: 'Navn', type: 'text', autoComplete: 'name', placeholder: 'Dit navn' },
  { name: 'phone', label: 'Telefonnummer', type: 'tel', autoComplete: 'tel', placeholder: 'Dit telefonnummer' },
  { name: 'email', label: 'Email', type: 'email', autoComplete: 'email', placeholder: 'Din e-mailadresse' },
];

function validate(values) {
  const errors = {};
  if (!String(values.name || '').trim()) errors.name = 'Skriv dit navn.';
  const phone = String(values.phone || '').trim();
  if (!phone) errors.phone = 'Skriv dit telefonnummer.';
  else if (phone.replace(/\D/g, '').length < 8) errors.phone = 'Indtast et gyldigt telefonnummer med mindst 8 cifre.';
  const email = String(values.email || '').trim();
  if (!email) errors.email = 'Skriv din e-mailadresse.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Indtast en gyldig e-mailadresse.';
  if (!String(values.description || '').trim()) errors.description = 'Fortæl kort, hvad du gerne vil tale om.';
  return errors;
}

export default function TalkPage({ page }) {
  const { pathFor } = useSite();
  const [status, setStatus] = useState('idle');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const inputs = useRef({});

  const clearError = (field) => {
    setErrors((current) => ({ ...current, [field]: '' }));
    if (status === 'error') { setStatus('idle'); setError(''); }
  };

  const submit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const values = Object.fromEntries(new FormData(form));
    const fieldErrors = validate(values);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length) {
      setError('Tjek de markerede felter, og prøv igen.');
      setStatus('error');
      inputs.current[Object.keys(fieldErrors)[0]]?.focus();
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: String(values.name).trim(),
          phone: String(values.phone).trim(),
          email: String(values.email).trim(),
          description: String(values.description).trim(),
          website_url: String(values.website_url || ''),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status !== 'ok') throw new Error(response.status === 400 ? 'Kontrollér oplysningerne, og prøv igen.' : 'Beskeden kunne ikke sendes. Prøv igen eller skriv direkte til os.');
      form.reset();
      setStatus('success');
    } catch (cause) {
      setError(cause.message || 'Noget gik galt. Prøv venligst igen.');
      setStatus('error');
    }
  };

  return <>
    <PageHero page={page} eyebrow="UDDANNELSEN · FØRSTE SKRIDT" action={false} compact imgClassName="object-top" />
    <section className="bg-paper py-16 sm:py-24">
      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-[.85fr_1.15fr] lg:gap-20 lg:px-12">
        <div>
          <Eyebrow>En uforpligtende snak</Eyebrow>
          <h2 className="mt-6 text-[clamp(2.5rem,4.5vw,5rem)] leading-[1.08] font-extrabold tracking-[-.06em] text-ink">Fortæl lidt om din plan.</h2>
          <RichText value={page.body} className="mt-7 max-w-md text-base text-body" />
          <div className="mt-12 border-t border-ink/20 pt-7"><span className="font-editorial text-3xl italic text-teal-dark">10 måneder.</span><p className="mt-3 max-w-sm text-sm leading-7 text-body">Et forløb med plads til at lære håndværket og arbejde med din egen salon. Samtalen hjælper dig med at finde ud af, om det er det rigtige for dig.</p><TextLink to={pathFor(PAGE.education)} className="mt-6">Læs om uddannelsen</TextLink></div>
        </div>
        <div className="border-t-2 border-teal bg-cream px-5 py-8 sm:px-9 sm:py-10">
          <h2 className="text-2xl font-extrabold tracking-[-.04em] text-ink sm:text-3xl">Book en uforpligtende samtale</h2>
          <p className="mt-3 text-sm leading-7 text-body">Udfyld de fire felter, så vender Salon K9 tilbage til dig.</p>
          {status === 'success' ? <div role="status" aria-live="polite" className="mt-10 border-l-4 border-teal bg-mist px-6 py-8"><h3 className="text-2xl font-extrabold text-ink">Tak for din besked.</h3><p className="mt-3 text-sm leading-7 text-body">Vi har modtaget din forespørgsel og vender tilbage om en samtale.</p><ActionLink to={pathFor(PAGE.home)} variant="subtle" className="mt-5 px-0">Gå til forsiden</ActionLink></div> :
            <form onSubmit={submit} noValidate className="mt-9 space-y-6">
              <div className="sr-only absolute" aria-hidden="true"><label htmlFor="website_url">Lad dette felt være tomt</label><input id="website_url" name="website_url" type="text" tabIndex={-1} autoComplete="off" /></div>
              {fields.map((field) => <div key={field.name}>
                <label htmlFor={`contact-${field.name}`} className="mb-2 block text-xs font-extrabold uppercase tracking-[.1em] text-ink">{field.label} <span aria-hidden="true" className="text-teal-dark">*</span></label>
                <input ref={(node) => { inputs.current[field.name] = node; }} id={`contact-${field.name}`} name={field.name} type={field.type} autoComplete={field.autoComplete} placeholder={field.placeholder} required maxLength={field.name === 'email' ? 254 : 120} onChange={() => clearError(field.name)} aria-invalid={Boolean(errors[field.name])} aria-describedby={errors[field.name] ? `error-${field.name}` : undefined} className={`h-14 w-full min-w-0 border bg-paper px-4 text-[15px] text-ink outline-none transition-colors placeholder:text-body/60 focus:ring-2 focus:ring-teal/25 ${errors[field.name] ? 'border-[#a74736]' : 'border-ink/20 focus:border-teal'}`} />
                {errors[field.name] && <p id={`error-${field.name}`} role="alert" className="mt-2 text-sm text-[#8d2c20]">{errors[field.name]}</p>}
              </div>)}
              <div>
                <label htmlFor="contact-description" className="mb-2 block text-xs font-extrabold uppercase tracking-[.1em] text-ink">Beskrivelse <span aria-hidden="true" className="text-teal-dark">*</span></label>
                <textarea ref={(node) => { inputs.current.description = node; }} id="contact-description" name="description" required rows={5} maxLength={5000} placeholder="Fortæl kort, hvad du gerne vil tale om" onChange={() => clearError('description')} aria-invalid={Boolean(errors.description)} aria-describedby={errors.description ? 'error-description' : undefined} className={`w-full min-w-0 resize-y border bg-paper px-4 py-4 text-[15px] text-ink outline-none transition-colors placeholder:text-body/60 focus:ring-2 focus:ring-teal/25 ${errors.description ? 'border-[#a74736]' : 'border-ink/20 focus:border-teal'}`} />
                {errors.description && <p id="error-description" role="alert" className="mt-2 text-sm text-[#8d2c20]">{errors.description}</p>}
              </div>
              {error && <p role="alert" className="border-l-2 border-[#a74736] pl-3 text-sm text-[#8d2c20]">{error}</p>}
              <button type="submit" disabled={status === 'sending'} className="inline-flex min-h-[54px] w-full items-center justify-center gap-3 bg-teal px-6 py-3 text-sm font-extrabold text-white transition-colors hover:bg-teal-dark focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal disabled:cursor-wait disabled:opacity-60 sm:w-auto">{status === 'sending' ? 'Sender…' : pick(page.cta_label) || 'Send din forespørgsel'}<i className="fa-regular fa-arrow-up-right text-xs" aria-hidden="true" /></button>
              <p className="text-xs leading-6 text-body">Vi bruger dine oplysninger til at vende tilbage om din henvendelse. Samtalen er uforpligtende.</p>
            </form>}
        </div>
      </div>
    </section>
  </>;
}
